import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../../styles/views.css';
import {
  FaUsers,
  FaMoneyBillWave,
  FaReceipt,
  FaLayerGroup,
  FaCashRegister,
  FaUserPlus,
  FaUpload,
  FaChartBar
} from 'react-icons/fa';
import ChartModal from '../../components/ChartModal';
import SupportChartModal from '../../components/SupportChartModal';

const DashboardView = ({ userInfo, API_BASE_URL, auth, onViewChange }) => {
  const [stats, setStats] = useState({
    totalTraders: 0,
    totalGroups: 0,
    todayCollections: 0,
    todayAmount: 0,
    totalReceipts: 0
  });
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [currentTarget, setCurrentTarget] = useState(null);
  const [currentPerformance, setCurrentPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [isSupportChartModalOpen, setIsSupportChartModalOpen] = useState(false);

  // Detect screen width for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Quick Actions navigation handlers
  const handleQuickAction = (action) => {
    switch (action) {
      case 'revenue-collection':
        onViewChange('Revenue Collection');
        break;
      case 'add-trader':
        onViewChange('Traders');
        break;
      case 'upload-traders':
        onViewChange('Upload Traders');
        break;
      case 'view-reports':
        setIsChartModalOpen(true);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleSupportChartOpen = () => {
    setIsSupportChartModalOpen(true);
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Debug: Log user info


      // Validate zone assignment
      if (!userInfo.zone_id) {

        setStats({
          totalTraders: 0,
          totalGroups: 0,
          todayCollections: 0,
          todayAmount: 0,
          totalReceipts: 0
        });
        setRecentTransactions([]);
        setLoading(false);
        return;
      }



      // Fetch traders in officer's zone

      const tradersResponse = await axios.get(
        `${API_BASE_URL}/traders?userRole=Revenue Officer&zone_id=${userInfo.zone_id}`,
        auth
      );


      // Fetch groups in officer's zone

      const groupsResponse = await axios.get(
        `${API_BASE_URL}/zonal-groups?zone_id=${userInfo.zone_id}`,
        auth
      );


      // Fetch today's transactions
      const today = new Date().toISOString().split('T')[0];

      const transactionsResponse = await axios.get(
        `${API_BASE_URL}/transactions?userRole=Revenue Officer&user_id=${userInfo.user_id}&date=${today}`,
        auth
      );


      // Try to fetch revenue collection stats (may not exist)
      let collectionStatsResponse = { data: [] };
      try {

        collectionStatsResponse = await axios.get(
          `${API_BASE_URL}/revenue-collection/stats?officer_id=${userInfo.user_id}`,
          auth
        );

      } catch (statsError) {
        console.log('Dashboard - Collection stats endpoint not available:', statsError.message);
      }



      // Calculate stats
      const calculatedStats = {
        totalTraders: tradersResponse.data?.length || 0,
        totalGroups: groupsResponse.data?.length || 0,
        todayCollections: transactionsResponse.data?.length || 0,
        todayAmount: transactionsResponse.data?.reduce((sum, t) => sum + parseFloat(t.total_amount || t.amount || 0), 0) || 0,
        totalReceipts: transactionsResponse.data?.filter(t => t.status === 'successful').length || 0
      };


      setStats(calculatedStats);

      setRecentTransactions(transactionsResponse.data?.slice(0, 5) || []);

      // Fetch targets and performance data
      if (userInfo.officer_id) {
        try {
          const performanceResponse = await axios.get(
            `${API_BASE_URL}/officer-performance/officer/${userInfo.officer_id}`,
            auth
          );

          const performanceData = performanceResponse.data;
          setCurrentPerformance(performanceData.current_performance);

          // Find current month target
          const currentMonth = new Date().toISOString().slice(0, 7);
          const monthlyTarget = performanceData.targets?.find(target =>
            target.target_period === currentMonth &&
            target.target_type === 'monthly' &&
            target.status === 'active'
          );
          setCurrentTarget(monthlyTarget);
        } catch (targetError) {
          console.log('Dashboard - Targets data not available:', targetError.message);
        }
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', error.response?.data || error.message);

      // Set fallback stats to avoid showing undefined
      setStats({
        totalTraders: 0,
        totalGroups: 0,
        todayCollections: 0,
        todayAmount: 0,
        totalReceipts: 0
      });
      setRecentTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [userInfo, API_BASE_URL, auth]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);



  const StatCard = ({ icon: Icon, title, value, subtitle, color = '#3498db' }) => (
    <div style={{
      backgroundColor: 'white',
      padding: '1rem',
      borderRadius: '0.5rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      textAlign: 'center',
      minHeight: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div style={{
        backgroundColor: color,
        color: 'white',
        padding: '0.75rem',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '0.75rem'
      }}>
        <Icon size={20} />
      </div>
      <div>
        <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: '#2c3e50' }}>
          {value !== undefined && value !== null ? value : '0'}
        </h3>
        <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#7f8c8d', fontWeight: '500' }}>
          {title}
        </p>
        {subtitle && (
          <p style={{ margin: '0.1rem 0 0 0', fontSize: '0.7rem', color: '#95a5a6' }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Welcome Section */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: '600', color: '#2c3e50' }}>
          Welcome back, {userInfo.full_name || userInfo.name}!
        </h1>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '1rem', color: '#7f8c8d' }}>
          Here's your revenue collection overview for today
        </p>
      </div>

      {/* Quick Actions */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.3rem', fontWeight: '600', color: '#2c3e50' }}>
           Quick Actions
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: '0.75rem',
          alignItems: 'stretch'
        }}>
          <button
            onClick={() => handleQuickAction('revenue-collection')}
            style={{
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '0.4rem',
              padding: '0.75rem 0.5rem',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              minHeight: '70px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#2980b9';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#3498db';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <FaCashRegister size={16} />
            <span style={{ fontSize: '0.75rem', lineHeight: '1.1' }}>Collections</span>
          </button>
          <button
            onClick={() => handleQuickAction('add-trader')}
            style={{
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '0.4rem',
              padding: '0.75rem 0.5rem',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              minHeight: '70px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#229954';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#27ae60';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <FaUserPlus size={16} />
            <span style={{ fontSize: '0.75rem', lineHeight: '1.1' }}>Add Trader</span>
          </button>
          <button
            onClick={() => handleQuickAction('upload-traders')}
            style={{
              backgroundColor: '#f39c12',
              color: 'white',
              border: 'none',
              borderRadius: '0.4rem',
              padding: '0.75rem 0.5rem',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              minHeight: '70px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#e67e22';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f39c12';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <FaUpload size={16} />
            <span style={{ fontSize: '0.75rem', lineHeight: '1.1' }}>Upload Traders</span>
          </button>
          <button
            onClick={() => handleQuickAction('view-reports')}
            style={{
              backgroundColor: '#9b59b6',
              color: 'white',
              border: 'none',
              borderRadius: '0.4rem',
              padding: '0.75rem 0.5rem',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              minHeight: '70px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#8e44ad';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#9b59b6';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <FaChartBar size={16} />
            <span style={{ fontSize: '0.75rem', lineHeight: '1.1' }}>View Reports</span>
          </button>
        </div>
      </div>

      {/* Key Performance Metrics */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{
          margin: '0 0 1.5rem 0',
          fontSize: '1.3rem',
          fontWeight: '600',
          color: '#2c3e50',
          textAlign: 'center'
        }}>
          📊 Daily Performance Overview
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(150px, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          <StatCard
            icon={FaUsers}
            title="Total Traders"
            value={stats.totalTraders}
            subtitle="In your zone"
            color="#3498db"
          />
          <StatCard
            icon={FaLayerGroup}
            title="Trader Groups"
            value={stats.totalGroups}
            subtitle="Active groups"
            color="#9b59b6"
          />
          <StatCard
            icon={FaMoneyBillWave}
            title="Today's Collections"
            value={stats.todayCollections || 0}
            subtitle={`GHS ${(stats.todayAmount || 0).toFixed(2)} collected`}
            color="#27ae60"
          />
          <StatCard
            icon={FaReceipt}
            title="Receipts Issued"
            value={stats.totalReceipts || 0}
            subtitle="Today"
            color="#f39c12"
          />
        </div>

        {/* Summary Bar */}
        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '0.5rem',
          border: '1px solid #e9ecef'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            gap: '1rem'
          }}>
            <div style={{ textAlign: 'center', flex: 1, minWidth: '120px' }}>
              <div style={{ fontSize: '0.8rem', color: '#6c757d', fontWeight: '500' }}>TRADERS</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#3498db' }}>
                {stats.totalTraders || 0}
              </div>
            </div>
            <div style={{ textAlign: 'center', flex: 1, minWidth: '120px' }}>
              <div style={{ fontSize: '0.8rem', color: '#6c757d', fontWeight: '500' }}>GROUPS</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#9b59b6' }}>
                {stats.totalGroups || 0}
              </div>
            </div>
            <div style={{ textAlign: 'center', flex: 1, minWidth: '120px' }}>
              <div style={{ fontSize: '0.8rem', color: '#6c757d', fontWeight: '500' }}>COLLECTIONS</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#27ae60' }}>
                {stats.todayCollections || 0}
              </div>
            </div>
            <div style={{ textAlign: 'center', flex: 1, minWidth: '120px' }}>
              <div style={{ fontSize: '0.8rem', color: '#6c757d', fontWeight: '500' }}>RECEIPTS</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#f39c12' }}>
                {stats.totalReceipts || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Target Overview */}
      {currentTarget && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '1.3rem',
              fontWeight: '600',
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🎯 Current Month Target
            </h2>
            <button
              onClick={() => onViewChange('Targets')}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
            >
              View All Targets
            </button>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: '1rem'
          }}>
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '0.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                TARGET
              </div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#3498db' }}>
                GHS {parseFloat(currentTarget.target_amount || 0).toLocaleString()}
              </div>
            </div>

            {currentPerformance && (
              <>
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '0.5rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                    COLLECTED
                  </div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#27ae60' }}>
                    GHS {parseFloat(currentPerformance.total_collected || 0).toLocaleString()}
                  </div>
                </div>

                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '0.5rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                    ACHIEVEMENT
                  </div>
                  <div style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: currentPerformance.achievement_percentage >= 100 ? '#27ae60' : '#f39c12'
                  }}>
                    {parseFloat(currentPerformance.achievement_percentage || 0).toFixed(1)}%
                  </div>
                </div>

                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '0.5rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                    REMAINING
                  </div>
                  <div style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: '#e74c3c'
                  }}>
                    GHS {Math.max(0, (currentTarget.target_amount || 0) - (currentPerformance.total_collected || 0)).toLocaleString()}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Progress Bar */}
          {currentPerformance && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#2c3e50' }}>
                  Progress to Target
                </span>
                <span style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                  {parseFloat(currentPerformance.achievement_percentage || 0).toFixed(1)}%
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e9ecef',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${Math.min(currentPerformance.achievement_percentage || 0, 100)}%`,
                  height: '100%',
                  backgroundColor: currentPerformance.achievement_percentage >= 100 ? '#27ae60' : '#3498db',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent Transactions */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.3rem', fontWeight: '600', color: '#2c3e50' }}>
          Recent Transactions
        </h2>
        
        {recentTransactions.length === 0 ? (
          <p style={{ color: '#7f8c8d', fontStyle: 'italic' }}>
            No transactions today. Start collecting revenue!
          </p>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '600px' : 'auto' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Time
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Payer
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Amount
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((transaction, index) => (
                  <tr key={index}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      {new Date(transaction.transaction_time).toLocaleTimeString()}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      {transaction.payer_name || 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      GHS {parseFloat(transaction.amount || 0).toFixed(2)}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: transaction.status === 'successful' ? '#d4edda' : '#f8d7da',
                        color: transaction.status === 'successful' ? '#155724' : '#721c24'
                      }}>
                        {transaction.status === 'successful' ? 'Success' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Chart Modal */}
      <ChartModal
        isOpen={isChartModalOpen}
        onClose={() => setIsChartModalOpen(false)}
        userRole="Field Officer"
        userInfo={userInfo}
        dashboardData={{}}
        API_BASE_URL={API_BASE_URL}
        auth={auth}
        onSupportChartOpen={handleSupportChartOpen}
      />

      {/* Support Chart Modal */}
      <SupportChartModal
        isOpen={isSupportChartModalOpen}
        onClose={() => setIsSupportChartModalOpen(false)}
        userRole="Field Officer"
        userInfo={userInfo}
        API_BASE_URL={API_BASE_URL}
        auth={auth}
      />

    </div>
  );
};

export default DashboardView;
